import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { couponAPI } from '../../services/couponAPI';

interface CouponState {
  coupons: any[];
  selectedCoupon: any | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: CouponState = {
  coupons: [],
  selectedCoupon: null,
  isLoading: false,
  error: null,
};

export const fetchCoupons = createAsyncThunk('coupons/fetchCoupons', async () => {
  return await couponAPI.getAllCoupons();
});

export const fetchCouponDetails = createAsyncThunk('coupons/fetchCouponDetails', async (id: string) => {
  return await couponAPI.getCouponById(id);
});

export const createCoupon = createAsyncThunk('coupons/createCoupon', async (data: any) => {
  return await couponAPI.createCoupon(data);
});

export const updateCoupon = createAsyncThunk('coupons/updateCoupon', async ({ id, data }: { id: string; data: any }) => {
  return await couponAPI.updateCoupon(id, data);
});

export const deleteCoupon = createAsyncThunk('coupons/deleteCoupon', async (id: string) => {
  await couponAPI.deleteCoupon(id);
  return id;
});

const couponSlice = createSlice({
  name: 'coupons',
  initialState,
  reducers: { clearError: (state) => { state.error = null; } },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCoupons.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCoupons.fulfilled, (state, action) => {
        console.log('Coupons API response:', action.payload);
        // Handle different response structures
        if (action.payload.coupons) {
          state.coupons = action.payload.coupons;
        } else if (action.payload.data) {
          state.coupons = action.payload.data;
        } else if (Array.isArray(action.payload)) {
          state.coupons = action.payload;
        } else {
          state.coupons = [];
        }
        state.isLoading = false;
        console.log('Coupons set to:', state.coupons);
      })
      .addCase(fetchCoupons.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch coupons';
      })
      .addCase(fetchCouponDetails.fulfilled, (state, action) => {
        state.selectedCoupon = action.payload.coupon || action.payload.data || action.payload;
        state.isLoading = false;
      })
      .addCase(createCoupon.fulfilled, (state, action) => {
        console.log('Create coupon response:', action.payload);
        const newCoupon = action.payload.coupon || action.payload.data || action.payload;
        state.coupons.unshift(newCoupon);
      })
      .addCase(updateCoupon.fulfilled, (state, action) => {
        console.log('Update coupon response:', action.payload);
        const updatedCoupon = action.payload.coupon || action.payload.data || action.payload;
        const index = state.coupons.findIndex(c => c._id === updatedCoupon._id);
        if (index !== -1) state.coupons[index] = updatedCoupon;
      })
      .addCase(deleteCoupon.fulfilled, (state, action) => {
        state.coupons = state.coupons.filter(c => c._id !== action.payload);
      });
  },
});

export default couponSlice.reducer;